/**
 * assets/custom-pricing.js  v2.1
 *
 * UPDATED: sqft_qty mode now uses AREA-BASED brackets instead of qty-based
 *
 * Formula-driven dynamic pricing for Shopify (no third-party apps).
 * Compatible with OS 2.0 / Dawn themes AND legacy jQuery-AJAX themes (Gecko, etc.).
 *
 * FORMULA ENGINE — UPDATED FOR sqft_qty
 * ======================================
 * For sqft_qty mode:
 *   - Brackets are determined by TOTAL AREA (width × length), NOT quantity
 *   - Each segment has area_max (was qty_max) and rate ($/sqft)
 *   - Formula: total = area * rate_for_area_bracket * qty + fee
 *
 * Example JSON:
 * {
 *   "mode": "sqft_qty",
 *   "min_width": 1,
 *   "min_length": 1,
 *   "min_qty": 100,
 *   "max_qty": 5000,
 *   "segments": [
 *     { "area_max": 25,  "rate": 5,    "fee": 0 },   // up to 25 sqft: $5/sqft
 *     { "area_max": 50,  "rate": 3,    "fee": 0 },   // 26-50 sqft: $3/sqft
 *     { "area_max": 100, "rate": 2,    "fee": 0 }    // 51-100 sqft: $2/sqft
 *   ]
 * }
 *
 * Calculation example:
 *   User enters: width=6ft, length=4ft, qty=1
 *   area = 24 sqft
 *   Bracket: 24 <= 25, so use segment 0: rate=$5/sqft, fee=$0
 *   total = 24 * 5 * 1 + 0 = $120
 */

(function () {
  'use strict';

  const SELECTOR  = '[data-custom-pricing]';
  const DEBOUNCE  = 150; // ms

  // ─── Helpers ───────────────────────────────────────────────────────────────

  function debounce(fn, ms) {
    let t;
    return function (...a) { clearTimeout(t); t = setTimeout(() => fn.apply(this, a), ms); };
  }

  function fmt(amount, symbol) {
    return `${symbol}${(isFinite(amount) ? amount : 0).toFixed(2)}`;
  }

  // ─── Formula engine ────────────────────────────────────────────────────────

  /**
   * Evaluates the pricing formula.
   * 
   * For sqft_qty mode: brackets are based on AREA (width × length), not quantity.
   * For other modes: original cumulative-band logic applies.
   *
   * Returns { total, area, tier, rate, breakdown } on success,
   * or { error: string } on validation failure.
   */
  function evaluate(formula, width, length, qty) {
    const {
      mode        = 'sqft_qty',
      min_width   = 0,
      min_length  = 0,
      min_qty     = 1,
      max_qty,
      segments    = [],
    } = formula;

    // ── Validation ────────────────────────────────────────────────────────────
    if ((mode === 'sqft_qty' || mode === 'sqft_only') && width < min_width)
      return { error: `Width must be at least ${min_width} ft` };
    if ((mode === 'sqft_qty' || mode === 'sqft_only') && length < min_length)
      return { error: `Length must be at least ${min_length} ft` };
    if ((mode === 'sqft_qty' || mode === 'qty_only') && qty < min_qty)
      return { error: `Minimum quantity is ${min_qty}` };
    if (max_qty && (mode === 'sqft_qty' || mode === 'qty_only') && qty > max_qty)
      return { error: `Maximum quantity is ${max_qty}` };
    if (segments.length === 0)
      return { error: 'No pricing formula configured for this product' };

    // ── Area ─────────────────────────────────────────────────────────────────
    const area = (mode === 'sqft_qty' || mode === 'sqft_only')
      ? Math.round(width * length * 100) / 100  // 2-decimal area
      : null;

    // ── AREA-BASED BRACKET LOGIC (NEW for sqft_qty) ──────────────────────────
    if (mode === 'sqft_qty') {
      // Find which bracket this area falls into
      let matchingSegment = null;
      for (const seg of segments) {
        if (area <= seg.area_max) {
          matchingSegment = seg;
          break;
        }
      }

      if (!matchingSegment) {
        const maxArea = segments[segments.length - 1].area_max;
        return { error: `Area of ${area} sqft exceeds maximum bracket of ${maxArea} sqft` };
      }

      // Calculate: total = area * rate * qty + fee
      const total = area * matchingSegment.rate * qty + matchingSegment.fee;
      return { 
        total, 
        area, 
        rate: matchingSegment.rate, 
        tier: matchingSegment,
        qty: qty
      };
    }

    // ── sqft_only MODE ────────────────────────────────────────────────────────
    if (mode === 'sqft_only') {
      // Find which bracket the area falls into (cumulative banding on area)
      let total = 0;
      let activeRate = segments[0].rate;
      let activeTier = segments[0];
      let prevMax = 0;

      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        const bandMax = seg.area_max || seg.qty_max; // backward compat
        const bandValue = (i === 0)
          ? Math.min(area, bandMax)
          : Math.min(area, bandMax) - prevMax;

        if (bandValue <= 0) { prevMax = bandMax; continue; }

        total += seg.rate * bandValue + seg.fee;
        activeRate = seg.rate;
        activeTier = seg;
        prevMax = bandMax;

        if (area <= bandMax) break;
      }

      return { total, area, rate: activeRate, tier: activeTier };
    }

    // ── qty_only MODE (unchanged) ─────────────────────────────────────────────
    if (mode === 'qty_only') {
      let total = 0;
      let activeRate = segments[0].rate;
      let activeTier = segments[0];
      let prevMax = 0;

      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        const bandMax = seg.qty_max;
        const bandValue = (i === 0)
          ? Math.min(qty, bandMax)
          : Math.min(qty, bandMax) - prevMax;

        if (bandValue <= 0) { prevMax = bandMax; continue; }

        total += seg.rate * bandValue + seg.fee;
        activeRate = seg.rate;
        activeTier = seg;
        prevMax = bandMax;

        if (qty <= bandMax) break;
      }

      return { total, area: null, rate: activeRate, tier: activeTier };
    }

    return { error: 'Unknown pricing mode' };
  }

  // ─── Main class ────────────────────────────────────────────────────────────

  class CustomPricing {
    constructor(root) {
      this.root       = root;
      this.mode       = root.dataset.pricingMode;
      this.unitValue  = parseFloat(root.dataset.unitValue) || 1.0;
      this.symbol     = this._detectSymbol();
      this.formula    = this._parseFormula(root);
      this.lastResult = null;
      this.form       = root.closest('form');

      this.els = {
        width:      root.querySelector('[data-pricing-input="width"]'),
        length:     root.querySelector('[data-pricing-input="length"]'),
        realQty:    root.querySelector('[data-pricing-input="real_quantity"]'),
        qtyDec:     root.querySelector('[data-pricing-qty-decrement]'),
        qtyInc:     root.querySelector('[data-pricing-qty-increment]'),
        outArea:    root.querySelector('[data-pricing-output="area"]'),
        outRate:    root.querySelector('[data-pricing-output="rate"]'),
        outTotal:   root.querySelector('[data-pricing-output="total"]'),
        error:      root.querySelector('[data-pricing-error]'),
        cartQty:    root.querySelector('[data-pricing-cart-quantity]'),
        props: {
          width:       root.querySelector('[data-pricing-prop="width"]'),
          length:      root.querySelector('[data-pricing-prop="length"]'),
          area:        root.querySelector('[data-pricing-prop="area"]'),
          tier_label:  root.querySelector('[data-pricing-prop="tier_label"]'),
          rate_label:  root.querySelector('[data-pricing-prop="rate_label"]'),
          real_qty:    root.querySelector('[data-pricing-prop="real_quantity_label"]'),
          total_label: root.querySelector('[data-pricing-prop="total_label"]'),
        },
      };

      root._cpInstance = this;
      this._bindEvents();
      this.recalculate();
    }

    _parseFormula(root) {
      const el = root.querySelector('[data-pricing-formula]');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        console.error('[custom-pricing] Could not parse formula JSON:', e);
        return null;
      }
    }

    _detectSymbol() {
      const el = document.querySelector(
        '[data-product-price], .price-item, .productPrice, .price'
      );
      if (el) {
        const m = el.textContent.match(/^[^\d.,\s]+/);
        if (m) return m[0];
      }
      return '$';
    }

    _bindEvents() {
      const recalc = debounce(() => this.recalculate(), DEBOUNCE);

      this.els.width   && this.els.width.addEventListener('input', recalc);
      this.els.length  && this.els.length.addEventListener('input', recalc);
      this.els.realQty && this.els.realQty.addEventListener('input', recalc);

      this.els.qtyDec && this.els.qtyDec.addEventListener('click', () => {
        const min = parseInt(this.els.realQty.min, 10) || 1;
        const cur = parseInt(this.els.realQty.value, 10) || min;
        this.els.realQty.value = Math.max(min, cur - 1);
        this.recalculate();
      });
      this.els.qtyInc && this.els.qtyInc.addEventListener('click', () => {
        const min = parseInt(this.els.realQty.min, 10) || 1;
        const cur = parseInt(this.els.realQty.value, 10) || min;
        this.els.realQty.value = cur + 1;
        this.recalculate();
      });

      if (!this.form) return;

      this.form.addEventListener('submit',
        (e) => this._beforeCartAdd(e, 'submit'), { capture: true });

      const btn = this.form.querySelector(
        '#shopify_add_to_cart, button[type="submit"], input[type="submit"]'
      );
      btn && btn.addEventListener('click',
        (e) => this._beforeCartAdd(e, 'click'), { capture: true });

      this._patchjQuery();
    }

    _patchjQuery() {
      if (!window.jQuery || window._cpAjaxPatched) return;
      window._cpAjaxPatched = true;

      const _orig = window.jQuery.ajax.bind(window.jQuery);
      window.jQuery.ajax = function (urlOrOpts, opts) {
        if (typeof urlOrOpts === 'object') { opts = urlOrOpts; urlOrOpts = undefined; }
        opts = opts || {};
        const url = urlOrOpts || opts.url || '';

        if (/\/cart\/add/.test(url)) {
          const el  = document.querySelector('[data-custom-pricing][data-pricing-initialized]');
          const ins = el && el._cpInstance;
          if (ins) {
            ins.recalculate();
            const r = ins.lastResult;

            if (!r || r.error || r.total <= 0) {
              ins.showError(r && r.error ? r.error : 'Please fill in all dimensions first.');
              const d = window.jQuery.Deferred();
              setTimeout(() => d.reject({ responseText: 'Custom pricing validation failed.' }), 0);
              return d.promise();
            }

            ins._applyToHiddenFields(r);

            if (typeof opts.data === 'string') {
              opts.data = ins._rewritePostData(opts.data, r);
            } else if (opts.data && typeof opts.data === 'object') {
              opts.data.quantity = Math.max(1, Math.round(r.total / ins.unitValue));
            }

            if (urlOrOpts) opts.url = url;
          }
        }

        return urlOrOpts !== undefined
          ? _orig(urlOrOpts, opts)
          : _orig(opts);
      };
    }

    // ── Calculation ──────────────────────────────────────────────────────────

    recalculate() {
      this.clearError();
      if (!this.formula) {
        this.showError('No pricing formula found on this product.');
        return;
      }

      const width  = parseFloat(this.els.width?.value)   || 0;
      const length = parseFloat(this.els.length?.value)  || 0;
      const qty    = parseInt(this.els.realQty?.value, 10) || 0;

      const needsW = this.mode === 'sqft_qty' || this.mode === 'sqft_only';
      const needsQ = this.mode === 'sqft_qty' || this.mode === 'qty_only';

      if ((needsW && (width <= 0 || length <= 0)) || (needsQ && qty <= 0)) {
        this._renderEmpty();
        return;
      }

      const result = evaluate(this.formula, width, length, qty);

      if (result.error) {
        this.showError(result.error);
        this._renderEmpty();
        this.lastResult = null;
        return;
      }

      result.width = width;
      result.length = length;
      result.qty = qty;
      this.lastResult = result;
      this._render(result);
    }

    _render(r) {
      if (this.els.outArea && r.area !== null) {
        this.els.outArea.textContent = `${r.area.toFixed(2)} sqft`;
      }
      if (this.els.outRate) {
        this.els.outRate.textContent = this.mode === 'qty_only'
          ? `${fmt(r.rate, this.symbol)} / item`
          : `${fmt(r.rate, this.symbol)} / sqft`;
      }
      if (this.els.outTotal) {
        this.els.outTotal.textContent = fmt(r.total, this.symbol);
      }
    }

    _renderEmpty() {
      if (this.els.outArea)  this.els.outArea.textContent  = '—';
      if (this.els.outRate)  this.els.outRate.textContent  = '—';
      if (this.els.outTotal) this.els.outTotal.textContent = '—';
    }

    showError(msg) {
      if (this.els.error) { this.els.error.textContent = msg; this.els.error.hidden = false; }
    }
    clearError() {
      if (this.els.error) { this.els.error.textContent = ''; this.els.error.hidden = true; }
    }

    // ── Cart interception ────────────────────────────────────────────────────

    _beforeCartAdd(e, trigger) {
      this.recalculate();
      const r = this.lastResult;

      if (!r || r.error || r.total <= 0) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.showError(r?.error || 'Please fill in all required fields before adding to cart.');
        return;
      }

      this._applyToHiddenFields(r);
    }

    _applyToHiddenFields(r) {
      const cartQty = Math.max(1, Math.round(r.total / this.unitValue));
      if (this.els.cartQty) this.els.cartQty.value = cartQty;

      const tierLabel = r.tier
        ? (this.mode === 'sqft_qty' 
          ? `Area up to ${r.tier.area_max} sqft (rate: ${fmt(r.rate, this.symbol)}/sqft)`
          : `Qty up to ${r.tier.qty_max} (rate: ${fmt(r.rate, this.symbol)})`)
        : '';

      const p = this.els.props;
      if (p.width && r.width != null)   p.width.value   = r.width.toFixed(2);
      if (p.length && r.length != null) p.length.value  = r.length.toFixed(2);
      if (p.area && r.area != null)     p.area.value    = r.area.toFixed(2);
      if (p.tier_label)    p.tier_label.value    = tierLabel;
      if (p.rate_label)    p.rate_label.value    = fmt(r.rate, this.symbol);
      if (p.real_qty)      p.real_qty.value      = r.qty ?? '';
      if (p.total_label)   p.total_label.value   = fmt(r.total, this.symbol);
    }

    _rewritePostData(str, r) {
      const cartQty = Math.max(1, Math.round(r.total / this.unitValue));
      const p = new URLSearchParams(str);
      p.set('quantity', cartQty);
      if (r.width  != null) p.set('properties[Width (ft)]',   r.width.toFixed(2));
      if (r.length != null) p.set('properties[Length (ft)]',  r.length.toFixed(2));
      if (r.area   != null) p.set('properties[Area (sqft)]',  r.area.toFixed(2));
      if (r.qty    != null) p.set('properties[Quantity Ordered]', r.qty);
      p.set('properties[Rate Applied]', fmt(r.rate, this.symbol));
      p.set('properties[Your Price]',   fmt(r.total, this.symbol));
      return p.toString();
    }
  }

  // ─── Init ──────────────────────────────────────────────────────────────────

  function initAll(root = document) {
    root.querySelectorAll(SELECTOR).forEach((el) => {
      if (!el.dataset.pricingInitialized) {
        el.dataset.pricingInitialized = 'true';
        new CustomPricing(el);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('cart:refresh', () => initAll());
  window.CustomPricingInit = initAll;

})();

// ─────────────────────────────────────────────────────────────────────────────
// CART DISPLAY FIX (unchanged)
// ─────────────────────────────────────────────────────────────────────────────

(function () {
  function fixCartDisplay() {
    document.querySelectorAll('[data-cp-line]').forEach((line) => {
      const props  = {};
      line.querySelectorAll('[data-cp-prop]').forEach((el) => {
        props[el.dataset.cpProp] = el.textContent.trim();
      });

      if (!props['Your Price']) return;

      const qtyEl = line.querySelector('[data-cp-qty]');
      if (qtyEl && props['Quantity Ordered']) {
        qtyEl.textContent = props['Quantity Ordered'];
      }

      const priceEl = line.querySelector('[data-cp-price]');
      if (priceEl) {
        priceEl.textContent = props['Your Price'];
      }

      const unitPriceEl = line.querySelector('[data-cp-unit-price]');
      if (unitPriceEl) unitPriceEl.style.display = 'none';
    });
  }

  document.addEventListener('DOMContentLoaded', fixCartDisplay);
  document.addEventListener('cart:refresh', fixCartDisplay);

  const cartContainers = ['#shopify-cart', '.jas_mini_cart', '[data-cart-drawer]',
                           '#mini-cart', '.cart-drawer', '#CartDrawer'];
  cartContainers.forEach((sel) => {
    const el = document.querySelector(sel);
    if (!el) return;
    new MutationObserver(fixCartDisplay).observe(el, { childList: true, subtree: true });
  });

  window.CustomPricingCartFix = fixCartDisplay;
})();

// ─────────────────────────────────────────────────────────────────────────────
// LABEL MATRIX & sqft_only MODES (unchanged from original)
// ─────────────────────────────────────────────────────────────────────────────

(function () {
  'use strict';

  function evaluateLabelMatrix(formula, width, length, qty, materialValue) {
    const { rules, production, materials, multipliers } = formula;

    const minW = rules.min_width  || 1;
    const minL = rules.min_length || 1;
    if (width  < minW) return { error: `Minimum width is ${minW}"` };
    if (length < minL) return { error: `Minimum length is ${minL}"` };

    const area = width * length;
    const smallThreshold = rules.small_label_threshold_sqin || 4;
    const smallMinQty    = rules.small_label_min_qty        || 500;
    const effectiveMinQty = area < smallThreshold ? smallMinQty : (rules.min_qty || 250);

    if (!qty || qty < effectiveMinQty) {
      return { error: area < smallThreshold
        ? `Labels under ${smallThreshold} sq in require a minimum of ${smallMinQty}`
        : `Minimum quantity is ${effectiveMinQty}` };
    }
    if (qty > (rules.max_qty || 2500))
      return { error: `Maximum quantity is ${rules.max_qty || 2500}` };

    const rollWidth = production.roll_width_inches || 7.5;
    const rowGap    = production.row_spacing_gap   || 0.125;

    const acrossA = Math.max(1, Math.floor(rollWidth / width));
    const rowsA   = Math.ceil(qty / acrossA);
    const feetA   = Math.ceil((rowsA * (length + rowGap)) / 12);

    const acrossB = Math.max(1, Math.floor(rollWidth / length));
    const rowsB   = Math.ceil(qty / acrossB);
    const feetB   = Math.ceil((rowsB * (width + rowGap)) / 12);

    const useA        = feetA <= feetB;
    const rollFeet    = useA ? feetA : feetB;
    const upsAcross   = useA ? acrossA : acrossB;
    const orientation = useA ? 'A' : 'B';

    const matEntry  = (materials || []).find(m => m.value === materialValue);
    const costPerFt = matEntry ? (matEntry.cost_per_foot || 0) : 0;
    const materialCost = rollFeet * costPerFt;

    const inkRate = production.ink_cost_per_sqin || 0.002;
    const inkCost = qty * width * length * inkRate;

    const rawCost = materialCost + inkCost;

    let priceTier = 0;
    for (const tier of multipliers) {
      if (rawCost <= tier.max) {
        priceTier = rawCost * tier.mult + (tier.add || 0);
        break;
      }
    }

    const lowOrderFee = priceTier < (rules.low_price_threshold || 175)
      ? (rules.low_price_fee || 25) : 0;

    const lowMaterialFee = materialCost < (rules.low_material_cost_threshold || 20)
      ? (rules.low_material_cost_fee || 25) : 0;

    const total = Math.max(
      rules.minimum_order_price || 55,
      priceTier + lowOrderFee + lowMaterialFee
    );

    return { total, rollFeet, upsAcross, orientation, materialCost, inkCost, rawCost, priceTier };
  }

  function patchLabelInstance(instance) {
    if (instance.mode !== 'label_matrix') return;

    instance.recalculate = function () {
      this.clearError();
      if (!this.formula) { this.showError('No pricing formula found.'); return; }

      const width    = parseFloat(this.els.width?.value)    || 0;
      const length   = parseFloat(this.els.length?.value)   || 0;
      const qty      = parseInt(this.els.realQty?.value, 10) || 0;
      const matEl    = this.root.querySelector('[data-pricing-input="material"]');
      const material = matEl ? matEl.value : 'Vinyl';

      if (!width || !length || !qty) { this._renderEmpty(); return; }

      const result = evaluateLabelMatrix(this.formula, width, length, qty, material);

      if (result.error) {
        this.showError(result.error);
        this._renderEmpty();
        this.lastResult = null;
        return;
      }

      result.width  = width;
      result.length = length;
      result.qty    = qty;
      result.mode   = 'label_matrix';
      this.lastResult = result;
      this._renderLabel(result);
    };

    instance._renderLabel = function (r) {
      if (this.els.outTotal) this.els.outTotal.textContent = `$${r.total.toFixed(2)}`;
    };

    const _origShowError = instance.showError.bind(instance);
    instance.showError = function (msg) {
      _origShowError(msg);
      if (this.mode !== 'label_matrix') return;
      if (this.els.outTotal) this.els.outTotal.textContent = '----';
    };

    instance._applyToHiddenFields = function (r) {
      if (r.mode !== 'label_matrix') return;
      const cartQty = Math.max(1, Math.round(r.total / this.unitValue));
      if (this.els.cartQty) this.els.cartQty.value = cartQty;
      const p = this.els.props;
      if (p.ups_label)          p.ups_label.value          = `${r.upsAcross} (orientation ${r.orientation})`;
      if (p.linfeet_label)      p.linfeet_label.value      = `${r.rollFeet} ft`;
      if (p.real_quantity_label) p.real_quantity_label.value = r.qty;
      if (p.total_label)        p.total_label.value        = `$${r.total.toFixed(2)}`;
    };

    instance.els.props.ups_label     = instance.root.querySelector('[data-pricing-prop="ups_label"]');
    instance.els.props.linfeet_label = instance.root.querySelector('[data-pricing-prop="linfeet_label"]');

    const matSel = instance.root.querySelector('[data-pricing-input="material"]');
    const finSel = instance.root.querySelector('[data-pricing-input="finish"]');
    if (matSel) matSel.addEventListener('change', () => instance.recalculate());
    if (finSel) finSel.addEventListener('change', () => instance.recalculate());

    instance.recalculate();
  }

  const _origInit = window.CustomPricingInit;
  window.CustomPricingInit = function (root) {
    _origInit && _origInit(root);
    (root || document)
      .querySelectorAll('[data-custom-pricing][data-pricing-mode="label_matrix"]')
      .forEach(el => { if (el._cpInstance) patchLabelInstance(el._cpInstance); });
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-custom-pricing][data-pricing-mode="label_matrix"]')
      .forEach(el => { if (el._cpInstance) patchLabelInstance(el._cpInstance); });
  });

})();